import { View } from '../components/orderhistory/types';
import { calculateKpiChange, KpiChangeData } from './kpiChangeCalculations';
import { filterOrdersByDateRange, filterRevenueByDateRange } from './dateRangeFilters';
import { calculateTotalSpent } from './orderHistoryCalculations';
import { calculateTotalRevenue } from './revenueHistoryCalculations';
import { OrderHistory } from '../stores/appOrderHistoryStore';
import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { TimeViewOption } from '../components/ui/TimeControls';
import { normalizeDate } from './dateUtils';
import { getWeek, getYear } from 'date-fns';

/**
 * General KPI calculations that combine multiple data sources
 */

/**
 * Calculate cost percentage (total spent / total revenue)
 */
export function calculateCostPercentage(
  totalSpent: number,
  totalRevenue: number
): number {
  return totalRevenue > 0 ? (totalSpent / totalRevenue) * 100 : 0;
}

/**
 * Bucket key for a date at the given granularity, mirroring the revenue-average
 * keys in revenueHistoryCalculations (day = ISO date, week = year*100+week,
 * month = `${year}-${monthIndex}`). Returns null for unsupported views.
 */
function bucketKeyForView(date: Date, view: TimeViewOption): string | null {
  const d = normalizeDate(new Date(date));
  switch (view) {
    case 'day':
      return d.toISOString().split('T')[0];
    case 'week':
      return String(getYear(d) * 100 + getWeek(d));
    case 'month':
      return `${getYear(d)}-${d.getMonth()}`;
    default:
      return null;
  }
}

/**
 * Average cost percentage across ALL historical buckets (day/week/month),
 * NOT the selected period — so it stays stable as you move back/forth in time,
 * exactly like the Revenue History page's average KPI. Each bucket's cost% is
 * spend ÷ revenue; the current (incomplete) bucket is excluded and only buckets
 * with revenue are averaged. Returns 0 (renders as "--") for the 'all' view.
 */
export function calculateAverageCostPercentage(
  orders: OrderHistory[],
  revenue: RevenueHistory[],
  showExVatOrders: boolean,
  showExVatRevenue: boolean,
  selectedTimeOption: TimeViewOption
): number {
  const currentKey = bucketKeyForView(new Date(), selectedTimeOption);
  if (currentKey === null) return 0; // 'all'/'quarter' → no stable average

  const spendByBucket = new Map<string, number>();
  orders.forEach(order => {
    const key = bucketKeyForView(new Date(order.orderDate), selectedTimeOption);
    if (key === null || key === currentKey) return;
    spendByBucket.set(key, (spendByBucket.get(key) || 0) + calculateTotalSpent([order], showExVatOrders));
  });

  const revenueByBucket = new Map<string, number>();
  revenue.forEach(entry => {
    const key = bucketKeyForView(new Date(entry.date), selectedTimeOption);
    if (key === null || key === currentKey) return;
    revenueByBucket.set(key, (revenueByBucket.get(key) || 0) + calculateTotalRevenue([entry], showExVatRevenue));
  });

  const costPercentages: number[] = [];
  revenueByBucket.forEach((bucketRevenue, key) => {
    if (bucketRevenue <= 0) return;
    const bucketSpend = spendByBucket.get(key) || 0;
    costPercentages.push((bucketSpend / bucketRevenue) * 100);
  });

  if (costPercentages.length === 0) return 0;
  return costPercentages.reduce((sum, v) => sum + v, 0) / costPercentages.length;
}

/**
 * Calculate food cost percentage (total food spent / total revenue)
 */
export function calculateFoodCostPercentage(
  totalFoodSpent: number,
  totalRevenue: number
): number {
  return totalRevenue > 0 ? (totalFoodSpent / totalRevenue) * 100 : 0;
}

/**
 * Calculate revenue change compared to previous period
 */
export function calculateRevenueChange(
  currentRevenue: number,
  view: View,
  startDate: Date,
  endDate: Date,
  revenueList: RevenueHistory[],
  showExVat: boolean
): KpiChangeData | null {
  const calculateRevenueForRange = (data: RevenueHistory[], start: Date, end: Date) => {
    const filtered = filterRevenueByDateRange(data, start, end);
    return calculateTotalRevenue(filtered, showExVat);
  };

  return calculateKpiChange(
    currentRevenue,
    view,
    startDate,
    endDate,
    calculateRevenueForRange,
    revenueList
  );
}

/**
 * Calculate cost change compared to previous period
 */
export function calculateCostChange(
  currentCost: number,
  view: View,
  startDate: Date,
  endDate: Date,
  orderList: OrderHistory[],
  showExVat: boolean
): KpiChangeData | null {
  const calculateCostForRange = (data: OrderHistory[], start: Date, end: Date) => {
    const filtered = filterOrdersByDateRange(data, start, end);
    return calculateTotalSpent(filtered, showExVat);
  };

  return calculateKpiChange(
    currentCost,
    view,
    startDate,
    endDate,
    calculateCostForRange,
    orderList
  );
}

/**
 * Calculate cost percentage change compared to previous period
 */
export function calculateCostPercentageChange(
  currentCostPercentage: number,
  view: View,
  startDate: Date,
  endDate: Date,
  orderList: OrderHistory[],
  revenueList: RevenueHistory[],
  showExVatOrders: boolean,
  showExVatRevenue: boolean
): KpiChangeData | null {
  const calculateCostPercentageForRange = (_: any[], start: Date, end: Date) => {
    const filteredOrders = filterOrdersByDateRange(orderList, start, end);
    const filteredRevenue = filterRevenueByDateRange(revenueList, start, end);
    const spent = calculateTotalSpent(filteredOrders, showExVatOrders);
    const revenue = calculateTotalRevenue(filteredRevenue, showExVatRevenue);
    return calculateCostPercentage(spent, revenue);
  };

  return calculateKpiChange(
    currentCostPercentage,
    view,
    startDate,
    endDate,
    calculateCostPercentageForRange,
    orderList
  );
}
